import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, Newspaper, ShieldCheck, Mail, Lock, LogIn, ChevronRight, Info } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState('');
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError('');
    try {
      await googleLogin(credentialResponse.credential);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Google login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google login failed. Please try again.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    console.log('🟡 LOGIN PAGE: Form submitted');
    console.log('🟡 LOGIN PAGE: Email =', email);
    console.log('🟡 LOGIN PAGE: Password =', password ? '***' + password.slice(-2) : 'empty');

    try {
      console.log('🟡 LOGIN PAGE: Calling login function...');
      await login(email, password);
      console.log('🟢 LOGIN PAGE: Login successful, navigating to home');
      navigate('/');
    } catch (err) {
      console.error('🔴 LOGIN PAGE: Login failed');
      console.error('🔴 LOGIN PAGE: Error =', err);
      const detail = err.response?.data?.detail;
      const errorMsg = err.response?.data?.error;
      
      if (errorMsg) {
        setError(errorMsg);
      } else if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map(d => d.msg || JSON.stringify(d)).join(' '));
      } else {
        setError('Invalid credentials. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center p-6 overflow-hidden bg-[#0A0C10]">
      {/* Background Aura Effects */}
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-[#D0021B]/10 blur-[120px] anima"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-indigo-600/10 blur-[120px]"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(circle_at_center,transparent_0%,rgba(10,12,16,0.8)_100%)]"></div>

      {/* Main Login Card */}
      <motion.div 
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="relative z-10 w-full max-w-[480px]"
      >
        <div className="group relative bg-white/[0.03] backdrop-blur-2xl border border-white/[0.08] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.6)] rounded-[2.5rem] overflow-hidden">
          {/* Glass Reflection Highlight */}
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.02] to-transparent pointer-events-none"></div>
          
          {/* Internal Progress Bar for Loading */}
          {loading && (
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              className="absolute top-0 left-0 h-1 bg-[#D0021B] shadow-[0_0_10px_#D0021B] z-20"
            />
          )}

          <div className="px-8 pt-12 pb-10">
            {/* Header Section */}
            <div className="text-center mb-10">
              <motion.div 
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="inline-flex p-4 rounded-3xl bg-gradient-to-br from-[#D0021B] to-[#FF4D4D] shadow-[0_8px_32px_rgba(208,2,27,0.3)] mb-6"
              >
                <Newspaper className="w-10 h-10 text-white" />
              </motion.div>
              
              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <h1 className="font-serif text-3xl font-bold text-white tracking-tight mb-2 uppercase">
                  FACT INDIA NEWS
                </h1>
                <p className="text-white/40 text-sm tracking-widest uppercase font-bold">
                  Press ID Card Management System
                </p>
              </motion.div>
            </div>


            {/* Error Message */}
            <AnimatePresence>
              {error && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-6 overflow-hidden"
                >
                  <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-200 text-sm flex items-center gap-3 backdrop-blur-sm">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                    {error}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="group relative">
                <label className={`absolute left-10 transition-all duration-300 pointer-events-none ${
                    isFocused === 'email' || email ? '-top-3 text-[10px] text-white font-bold px-2 py-0.5 bg-[#D0021B] rounded-md z-10 scale-100 shadow-lg' : 'top-4 text-sm text-white/40'
                  }`}
                >
                  EMAIL
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className={`w-5 h-5 transition-colors duration-300 ${isFocused === 'email' ? 'text-[#D0021B]' : 'text-white/20'}`} />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onFocus={() => setIsFocused('email')}
                    onBlur={() => setIsFocused('')}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 bg-white/[0.03] border border-white/10 focus:border-[#D0021B]/40 rounded-2xl text-white text-sm focus:bg-white/[0.05] focus:ring-4 focus:ring-[#D0021B]/5 transition-all outline-none"
                    placeholder={isFocused === 'email' ? "" : ""}
                    required
                  />
                </div>
              </div>

              <div className="group relative">
                <label className={`absolute left-10 transition-all duration-300 pointer-events-none ${
                    isFocused === 'password' || password ? '-top-3 text-[10px] text-white font-bold px-2 py-0.5 bg-[#D0021B] rounded-md z-10 scale-100 shadow-lg' : 'top-4 text-sm text-white/40'
                  }`}
                >
                  PASSWORD
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className={`w-5 h-5 transition-colors duration-300 ${isFocused === 'password' ? 'text-[#D0021B]' : 'text-white/20'}`} />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onFocus={() => setIsFocused('password')}
                    onBlur={() => setIsFocused('')}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-12 pr-12 py-4 bg-white/[0.03] border border-white/10 focus:border-[#D0021B]/40 rounded-2xl text-white text-sm focus:bg-white/[0.05] focus:ring-4 focus:ring-[#D0021B]/5 transition-all outline-none"
                    placeholder={isFocused === 'password' ? "••••••••" : ""}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/20 hover:text-white/60 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="mt-2 flex justify-end">
                  <Link 
                    to="/forgot-password" 
                    className="text-[10px] font-bold text-white/30 hover:text-[#D0021B] uppercase tracking-widest transition-colors"
                  >
                    Forgot Password?
                  </Link>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.01, y: -2 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="relative w-full py-4 bg-[#D0021B] text-white font-bold rounded-2xl shadow-[0_8px_32px_rgba(208,2,27,0.3)] hover:shadow-[0_12px_48px_rgba(208,2,27,0.45)] transition-all disabled:opacity-50 disabled:cursor-not-allowed group overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    AUTHENTICATING...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2 tracking-[0.1em] uppercase">
                    Login
                  
                  </span>
                )}
              </motion.button>
            </form>

            {/* Separator */}
            <div className="relative my-8 text-center">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <span className="relative px-4 text-[10px] text-white/80 font-bold uppercase tracking-widest">
                OR
              </span>
            </div>

            {/* Google Login Button Container */}
            <div className="flex justify-center">
              <div className="w-full relative group">
                {/* External Custom Wrapper to Match Styling */}
                <div className="flex justify-center w-full bg-white/[0.03] hover:bg-white/[0.05] border border-white/10 rounded-2xl py-1 transition-all">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    theme="filled_black"
                    shape="pill"
                    text="continue_with"
                    width="100%"
                    useOneTap
                  />
                </div>
              </div>
            </div>

            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-8 text-center"
            >
              <p className="text-white/40 text-sm">
                Don't have an account?{' '}
                <Link 
                  to="/register" 
                  className="text-[#D0021B] hover:text-[#FF4D4D] font-bold transition-colors"
                >
                  Create new
                </Link>
              </p>
            </motion.div>

            
          </div>
        </div>

        {/* Card Shadow/Glow Background */}
        <div className="absolute -inset-1 bg-gradient-to-r from-[#D0021B]/20 to-transparent rounded-[2.6rem] blur-2xl opacity-50 -z-10 group-hover:opacity-75 transition-opacity"></div>
        
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-8 flex flex-col items-center gap-4"
        >
         
        </motion.div>
      </motion.div>

      {/* Ambient Grain Overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] mix-blend-overlay bg-[url('https://grainy-gradients.vercel.app/noise.svg')]"></div>
    </div>
  );
}